/**
 * Ink for a heat-map cell (Decision Workspace screens only).
 *
 * The ported screens paint a rating with the source's fill AND the source's text colour; a few of
 * those pairs (white on #66bb6a, white on #fb8c00) measure below 4.5:1. The FILLS carry the
 * scale and are kept exactly. The ink is chosen per fill, whichever of near-black and white
 * measures higher. Two source fills (#e53935, #1A9850) reach only 3.7 to 4.4 with either ink; for
 * those, `heatCell` darkens the fill in small steps, keeping its hue, until white text reaches 4.5.
 * Every other fill is kept exactly.
 */

const DARK = '#1f1f1f';
const LIGHT = '#ffffff';

function channel(v: number): number {
  const c = v / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function luminance(hex: string): number {
  const h = hex.replace('#', '');
  const n = h.length === 3 ? h.replace(/./g, '$&$&') : h;
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** The ink that reads best on `fill`. */
export function inkOn(fill: string): string {
  return contrast(fill, DARK) >= contrast(fill, LIGHT) ? DARK : LIGHT;
}

const AA = 4.5;

/** Darken `hex` by `factor` per channel. */
function darken(hex: string, factor: number): string {
  const h = hex.replace('#', '');
  const part = (i: number) =>
    Math.round(parseInt(h.slice(i, i + 2), 16) * factor)
      .toString(16)
      .padStart(2, '0');
  return `#${part(0)}${part(2)}${part(4)}`;
}

/** The fill and ink to paint a heat cell: the source fill unless no ink passes AA on it. */
export function heatCell(fill: string): { bg: string; fg: string } {
  let bg = fill;
  for (let i = 0; i < 12; i += 1) {
    const fg = inkOn(bg);
    if (contrast(bg, fg) >= AA) return { bg, fg };
    bg = darken(bg, 0.94);
  }
  return { bg, fg: inkOn(bg) };
}
